/* eslint-disable complexity */
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';
import { ReportTemplateList } from '@futrli-sdk/predict';
import { WorkspaceMember } from '@futrli-sdk/people';

import { getUserName } from '../../utils/users/getUserName';

export interface ReportTemplateActions {
  delete: boolean;
  duplicate: boolean;
  edit: boolean;
  publish: boolean;
  share: boolean;
  unpublish: boolean;
}

export interface ExtendedReportTemplateList extends ReportTemplateList {
  actions: ReportTemplateActions;
  isOwner: boolean;
  ownerName?: string;
}

export const processReportTemplate = (
  template: ReportTemplateList,
  flags: LDFlagSet = {},
  currentUser?: CurrentUserCheck,
  members: WorkspaceMember[] = []
): ExtendedReportTemplateList => {
  const hasResourceSharingEnabled = !!flags.resourceSharing;
  const permissionInfo = hasResourceSharingEnabled ? template.permission_info : undefined;
  const ownerId = hasResourceSharingEnabled ? permissionInfo?.owner_id : template.owner_id;
  const owner = ownerId ? members.find(member => member.id === ownerId) : undefined;
  const hasViewPermission =
    !hasResourceSharingEnabled ||
    permissionInfo?.effective_permission === 'view' ||
    permissionInfo?.effective_permission === 'edit';
  const hasEditPermission = !hasResourceSharingEnabled || permissionInfo?.effective_permission === 'edit';
  const isFutrliTemplate = template.kind === 'futrli';
  const isPublished = template.status === 'published';

  return {
    ...template,
    isOwner: currentUser?.id === ownerId,
    ownerName: owner ? getUserName(owner.name) : undefined,
    actions: {
      delete: hasEditPermission,
      duplicate: hasViewPermission,
      edit: hasEditPermission,
      publish: (!hasResourceSharingEnabled || isFutrliTemplate) && !isPublished,
      share: hasResourceSharingEnabled && hasEditPermission && !isFutrliTemplate,
      unpublish: (!hasResourceSharingEnabled || isFutrliTemplate) && isPublished
    }
  };
};

export const processReportTemplates = (
  templates: ReportTemplateList[],
  flags: LDFlagSet = {},
  currentUser?: CurrentUserCheck,
  members: WorkspaceMember[] = []
): ExtendedReportTemplateList[] =>
  templates.map(template => processReportTemplate(template, flags, currentUser, members));
